import { Link, router } from '@inertiajs/react';
import { useState } from 'react';
import GmLayout from '@/layouts/GmLayout';

interface ChallengeSummary {
    id: number;
    name: string;
    status: {
        value: string;
        label: string;
    };
    enclave: {
        id: number;
        name: string;
        owner_name: string | null;
    } | null;
    targets_count: number;
    contributions_count: number;
    progress_percent: number;
    completed_at: string | null;
    created_at: string;
}

interface PaginationLink {
    url: string | null;
    label: string;
    active: boolean;
}

interface PaginatedChallenges {
    data: ChallengeSummary[];
    links: PaginationLink[];
    last_page: number;
}

interface Props {
    challenges: PaginatedChallenges;
    filters: {
        search: string | null;
        status: string | null;
    };
    statuses: Array<{ value: string; label: string }>;
}

export default function Index({ challenges, filters, statuses }: Props) {
    const [search, setSearch] = useState(filters.search ?? '');
    const [status, setStatus] = useState(filters.status ?? '');

    const applyFilters = () => {
        router.get('/gm/global-challenges', {
            search: search || undefined,
            status: status || undefined,
        }, { preserveState: true });
    };

    const clearFilters = () => {
        setSearch('');
        setStatus('');
        router.get('/gm/global-challenges', {}, { preserveState: true });
    };

    return (
        <GmLayout title="Global Challenges" activePath="/gm/global-challenges">
            <div className="max-w-7xl">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-white">Global Challenges</h1>
                    <Link
                        href="/gm/global-challenges/create"
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                    >
                        + Create Global Challenge
                    </Link>
                </div>

                <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 mb-6">
                    <div className="flex flex-wrap gap-4 items-end">
                        <div className="flex-1 min-w-[220px]">
                            <label className="block text-sm text-gray-400 mb-1">Search</label>
                            <input
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                onKeyDown={(event) => event.key === 'Enter' && applyFilters()}
                                placeholder="Challenge name or description"
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-gray-400"
                            />
                        </div>

                        <div className="w-52">
                            <label className="block text-sm text-gray-400 mb-1">Status</label>
                            <select
                                value={status}
                                onChange={(event) => setStatus(event.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                            >
                                <option value="">All</option>
                                {statuses.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={applyFilters}
                                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                            >
                                Apply
                            </button>
                            <button
                                type="button"
                                onClick={clearFilters}
                                className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                </div>

                {challenges.data.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">No global challenges found.</div>
                ) : (
                    <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-slate-700">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Challenge</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Enclave</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Progress</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Targets</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Contributions</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700">
                                    {challenges.data.map((challenge) => (
                                        <tr key={challenge.id}>
                                            <td className="px-4 py-3">
                                                <Link href={`/gm/global-challenges/${challenge.id}`} className="text-white font-medium hover:text-purple-300">
                                                    {challenge.name}
                                                </Link>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-300">
                                                {challenge.enclave?.owner_name || challenge.enclave?.name || 'Unknown'}
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="px-2 py-1 text-xs rounded bg-slate-600 text-gray-100">
                                                    {challenge.status.label}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-300">{challenge.progress_percent}%</td>
                                            <td className="px-4 py-3 text-sm text-gray-300">{challenge.targets_count}</td>
                                            <td className="px-4 py-3 text-sm text-gray-300">{challenge.contributions_count}</td>
                                            <td className="px-4 py-3">
                                                <Link
                                                    href={`/gm/global-challenges/${challenge.id}`}
                                                    className="text-sm text-purple-300 hover:text-purple-200"
                                                >
                                                    View
                                                </Link>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {challenges.last_page > 1 && (
                    <div className="flex flex-wrap gap-2 mt-6">
                        {challenges.links.map((link, index) => (
                            <button
                                key={`${link.label}-${index}`}
                                type="button"
                                disabled={!link.url}
                                onClick={() => link.url && router.visit(link.url)}
                                className={`px-3 py-1 rounded text-sm ${
                                    link.active
                                        ? 'bg-purple-600 text-white'
                                        : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                                } disabled:opacity-40 disabled:cursor-not-allowed`}
                                dangerouslySetInnerHTML={{ __html: link.label }}
                            />
                        ))}
                    </div>
                )}
            </div>
        </GmLayout>
    );
}
